<template>
	<div>
		<div class="application-source-selection">
			<div v-for="mapping in mappingsToShow" :key="mapping.originalKey">
				<SingleMappingEditor
					:mapping="mapping"
					:serviceName="service.service_name"
					:mappingOutputLabel="mappingValuesToOptions[mapping.originalKey]"
					@save-mapping="saveMapping"
					@remove-option="removeMapping"
				/>
			</div>
		</div>
		<div class="choose-params pt-8">
			<div v-if="mappingChoiceOptions.length > 0">
				<AppSelect
					class="more-fields"
					item-text="option"
					naked
					dense
					v-model="chosenMapOption"
					:items="mappingChoiceOptions"
					return-object
					@change="onChange"
					label="+ Add properties"
					ref="select"
				/>
			</div>
			<AppLoader v-else />
		</div>
	</div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { mapActions, mapGetters } from 'vuex';
import ParamsService from '@/utils/ParamsService';
import WidgetService from '@/utils/WidgetService';
import { saveWidget } from '@/store/modules/widgets/actions';
import { getWidgetFieldData } from '@/store/modules/widgets/getters';
import { editedWorkflowVersion } from '@/store/modules/selectedWorkflow/getters';
import { sourceForMappings } from '@/store/modules/selectedWorkflow/modules/userSchemaMapper/getters';
import { getSourceForMappingsInMapper, saveMappingsInResponse } from '@/store/modules/selectedWorkflow/modules/userSchemaMapper/actions';
import SingleMappingEditor from './SingleMappingEditor.vue';
import { getConfigurationAndInterfaceIdByFieldUUID } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { getConfigurationFieldResponse } from '@/store/modules/workflowConfigurations/getters';
import { convertArrayOfObjectsToObject } from '@/utils/helpers';

export default {
	name: 'DynamicParamsMapperDispatcher',
	props: {
		widgetFieldUUID: { type: String },
		service: { type: Object },
		fieldItem: { type: String },
	},
	components: {
		SingleMappingEditor,
	},
	computed: {
		...mapGetters([
			getWidgetFieldData.fullPath,
			editedWorkflowVersion.fullPath,
			sourceForMappings.fullPath,
			getConfigurationAndInterfaceIdByFieldUUID.fullPath,
			getConfigurationFieldResponse.fullPath,
		]),
		widgetFieldData() {
			return this[getWidgetFieldData.fullPath](this.widgetFieldUUID) || {};
		},
		mapperData() {
			return this.widgetFieldData.widgetField || {};
		},
		existingMappings() {
			return this.mapperData?.metadata?.source?.mappings || [];
		},
		existingMappingsKeys() {
			// current keys that cant be chosen again
			return this.mappingsToShow.map((x) => x.originalKey);
		},
		mappingOptionsKeys() {
			return this.mappingOptions.map((x) => x.value);
		},
		mappingsToShow() {
			return [...this.existingMappings, ...this.tempMapOptions];
		},
		mappingChoiceOptions() {
			return this.mappingOptions?.filter((m) => !this.existingMappingsKeys.includes(m.value));
		},
		sourceActionString() {
			return ParamsService.getParamsSourceForMapping(this.service, this.fieldItem);
		},
		mappingOptions() {
			return this[sourceForMappings.fullPath](this.widgetFieldUUID) || [];
		},
		configurationAndInterfaceIdByFieldUUID() {
			return this[getConfigurationAndInterfaceIdByFieldUUID.fullPath](this.widgetFieldUUID);
		},
		interface() {
			return this.configurationAndInterfaceIdByFieldUUID.interface;
		},
		configurationId() {
			return this.configurationAndInterfaceIdByFieldUUID.configurationId;
		},
		interfaceUUID() {
			return this.configurationAndInterfaceIdByFieldUUID.interfaceUUID;
		},
		creatorMapResponse() {
			return this[getConfigurationFieldResponse.fullPath](this.configurationId, this.widgetFieldUUID) || {};
		},
		mappingValuesToOptions() {
			return convertArrayOfObjectsToObject(this.mappingOptions, 'value', 'option');
		},
	},
	methods: {
		...mapActions([getSourceForMappingsInMapper.fullPath, saveWidget.fullPath, saveMappingsInResponse.fullPath]),
		async onChange(value) {
			this.chosenMapOption = null;
			this.$refs.select.blur();
			this.tempMapOptions = [{ displayName: '', originalKey: value.value }];
		},
		async saveMapping(mapping, remove = false) {
			this.tempMapOptions = [];
			const currentInterface = cloneDeep(this.interface);
			await WidgetService.saveMappingsInMapperWidgetField(
				mapping,
				currentInterface,
				this[editedWorkflowVersion.fullPath]?.id,
				this.widgetFieldUUID,
				this[saveWidget.fullPath],
				remove
			);
			await this.saveMappingsInResponse(mapping, remove);
		},
		async removeMapping(mapping) {
			await this.saveMapping(mapping, true);
		},
		async saveMappingsInResponse(value, remove) {
			await this[saveMappingsInResponse.fullPath]({
				configurationId: this.configurationId,
				interfaceUUID: this.interfaceUUID,
				widgetFieldUUID: this.widgetFieldUUID,
				value,
				remove,
			});
		},
	},
	async mounted() {
		this[getSourceForMappingsInMapper.fullPath]({
			widgetFieldUUID: this.widgetFieldUUID,
			sourceActionString: this.sourceActionString,
			searchString: this.sourceActionString,
			workflowVersionId: this[editedWorkflowVersion.fullPath]?.id,
		});
	},
	data() {
		return {
			chosenMapOption: null,
			tempMapOptions: [],
			localMappingOptions: [],
			searchString: '',
			loading: false,
		};
	},
};
</script>

<style lang="scss" scoped>
.choose-params {
	font-size: 11px;
}
.more-fields {
	background: var(--bg-color);
	border: 1px dashed var(--primary-color);
	.v-select.v-text-field input {
		color: var(--bg-color) !important;
	}
}
.option {
	font-size: 14px;
}
.required {
	color: grey;
	font-size: 11px;
}
</style>
